import React, { useCallback, useMemo, useState } from "react";
import { Button, colors, fontFamilies } from "@commonsku/styles";
import DropdownMenu, { MenuTrigger } from "../../../DropdownMenu";
import styled from "styled-components";
import {
  ActionType,
  PotentialRepeatReportAction,
  ProjectAction,
} from "./types";
import { createCopyOrderPopup } from "../../../../actions/popup";
import { useDispatch, useSelector } from "react-redux";
import { createLoadOrder } from "../../../../actions/order";
import { createLoadEventTypes } from "../../../../actions";
import NewProjectPopup from "../../../project/NewProjectPopup";

import { ORDER_TYPES, OrderType } from "../../columns/config/types";
import { titleCase } from "../../../../utils";
import { Order } from "../../../../interfaces/Order";
import { CreateTaskPopup } from "../../../message";
import { DisplayClient } from "../../../../types";
import {
  selectDismissed,
  setActionTaken,
} from "../../../../redux/potentialRepeatReport";
import { updateProject } from "../../../../actions/project";

// Type definitions for createLoadOrder return values
type LoadOrderResult = Order | { payload: { order: Order } } | null | undefined;

const isLoadOrderResult = (value: any): value is LoadOrderResult => {
  // Handle null/undefined
  if (value == null) return true;

  // Must be an object
  if (typeof value !== "object") return false;

  // Check if it's a Redux action with payload structure
  if (
    "payload" in value &&
    value.payload &&
    typeof value.payload === "object" &&
    "order" in value.payload &&
    typeof value.payload.order === "object" &&
    "order_id" in value.payload.order
  ) {
    return true;
  }

  // Check if it's a direct order object
  if ("order_id" in value && typeof value.order_id === "string") {
    return true;
  }

  return false;
};

/**
 * Extracts Order object from different possible return formats of createLoadOrder
 */
const extractOrderFromResult = (result: LoadOrderResult): Order | null => {
  if (!result || typeof result !== "object") return null;

  // Check if it's a Redux action with payload
  if ("payload" in result && result.payload?.order) {
    return result.payload.order;
  }

  // Check if it's a direct order object (has order_id property)
  if ("order_id" in result) {
    return result as Order;
  }

  return null;
};

const StyledDropdownMenu = styled(DropdownMenu)`
  & > .dropdown-menu-trigger {
    color: ${colors.primary1.main};
    border-radius: 5px;
    padding: 4px 4px 0 4px;
  }

  &&& > ul {
    border: 2px solid ${colors.primary1.main};
    border-radius: 5px;
    margin-top: 4px;
    right: 1rem;
    min-width: 170px;

    & > li {
      font-family: ${fontFamilies.regular};
      color: ${colors.neutrals.bodyText};
    }
  }
`;

const StyledActionsButton = styled(Button)`
  &&& {
    padding: 8px 24px;
  }
`;
export const POTENTIAL_REPEAT_REPORT_SOURCE = "POTENTIAL_REPEAT_REPORT";
interface ActionDropdownProps {
  order_id: string;
  dismissed: boolean;
  job_id: string;
  client_id: string;
  client_name: string;
  job_number: string;
  onActionClick?: (action: ActionType) => void;
  job_total_budget?: number;
  event_type_id?: string;
  order_type?: string;
  billing_contact_id?: string;
  public_view_layout_id?: string;
  public_view_template_id?: string;
  view_type?: string;
}

const buildCopyPopupOptions = (selectedType: OrderType) => {
  const action = { key: selectedType, value: titleCase(selectedType) };

  // Build result for all order types, each with the selectedType action
  const result: Record<
    OrderType,
    Array<{ key: string; value: string }>
  > = {} as any;
  Object.values(ORDER_TYPES).forEach((type) => {
    result[type] = [action];
  });

  return result;
};

export function ActionDropdown({
  dismissed,
  order_id,
  job_id,
  client_id,
  client_name,
  job_number,
  job_total_budget,
  event_type_id,
  order_type,
  billing_contact_id,
  public_view_layout_id,
  public_view_template_id,
  view_type
}: ActionDropdownProps) {
  const [taskPopup, setTaskPopup] = useState(false);
  const dispatch = useDispatch();
  const [showNewProjectPopup, setShowNewProjectPopup] = useState(false);
  const [newProjectPopupData, setNewProjectPopupData] = useState<any>(null);
  const isDismissedInAction = useSelector((state) =>
    selectDismissed(state, order_id)
  );

  const handleActionItemClick = useCallback(
    async (action: ActionType) => {
      let copy_order_popup_actions = {};
      let order: Order | null = null;

      if (order_id) {
        const rawResult = await dispatch(createLoadOrder(order_id));

        if (isLoadOrderResult(rawResult)) {
          order = extractOrderFromResult(rawResult);
        } else {
          console.warn(
            "createLoadOrder returned unexpected format:",
            rawResult
          );
        }
      }

      switch (action) {
        case PotentialRepeatReportAction.CREATE_TASK:
          setTaskPopup(true);
          break;
        case PotentialRepeatReportAction.CREATE_PROJECT:
          await dispatch(createLoadEventTypes());

          const prePopulatedData = {
            client_id: client_id,
            stage: order_type || 'OPPORTUNITY',
            billing_contact_id: billing_contact_id,
            total_budget: job_total_budget,
            view_type: view_type || order?.view_type,
            event_type_id: event_type_id,
            original_job_id: job_id,
            original_order_id: order_id,
            public_view_layout_id: public_view_layout_id,
            public_view_template_id: public_view_template_id,
          };

          setNewProjectPopupData(prePopulatedData);
          setShowNewProjectPopup(true);
          break;
        case PotentialRepeatReportAction.COPY_PRESENTATION:
          copy_order_popup_actions = buildCopyPopupOptions(
            ORDER_TYPES.PRESENTATION
          );
          if (order) {
            dispatch(
              createCopyOrderPopup(
                order,
                null,
                null,
                POTENTIAL_REPEAT_REPORT_SOURCE,
                copy_order_popup_actions,
                PotentialRepeatReportAction.COPY_PRESENTATION,
                ORDER_TYPES.PRESENTATION
              )
            );
          }
          break;
        case PotentialRepeatReportAction.COPY_SALES_ORDER:
          copy_order_popup_actions = buildCopyPopupOptions(
            ORDER_TYPES["SALES ORDER"]
          );
          if (order) {
            dispatch(
              createCopyOrderPopup(
                order,
                null,
                null,
                POTENTIAL_REPEAT_REPORT_SOURCE,
                copy_order_popup_actions,
                PotentialRepeatReportAction.COPY_SALES_ORDER,
                ORDER_TYPES["SALES ORDER"]
              )
            );
          }
          break;
        case PotentialRepeatReportAction.DISMISS:
          // Handle dismiss action

          dispatch(updateProject(job_id, "job_dismissed", false, true));
          dispatch(
            setActionTaken({
              orderId: order_id,
              isDismissed: true,
            })
          );
          break;
        case PotentialRepeatReportAction.UNDO_DISMISS:
          // Handle undo dismiss action
          dispatch(updateProject(job_id, "job_dismissed", true, false));
          dispatch(
            setActionTaken({
              orderId: order_id,
              isDismissed: false,
            })
          );
          break;
        default:
          break;
      }
    },
    [dispatch, job_id, order_id]
  );

  const actionDropdownOptions: {
    value: ActionType;
    onClick: () => void;
  }[] = useMemo(() => {
    let dismissedValue = dismissed
      ? PotentialRepeatReportAction.UNDO_DISMISS
      : PotentialRepeatReportAction.DISMISS;

    if (isDismissedInAction === true) {
      dismissedValue = PotentialRepeatReportAction.UNDO_DISMISS;
    }

    if (isDismissedInAction === false) {
      dismissedValue = PotentialRepeatReportAction.DISMISS;
    }

    return [
      {
        value: PotentialRepeatReportAction.CREATE_TASK,
        onClick: () =>
          handleActionItemClick(PotentialRepeatReportAction.CREATE_TASK),
      },
      {
        value: PotentialRepeatReportAction.CREATE_PROJECT,
        onClick: () =>
          handleActionItemClick(PotentialRepeatReportAction.CREATE_PROJECT),
      },
      {
        value: PotentialRepeatReportAction.COPY_PRESENTATION,
        onClick: () =>
          handleActionItemClick(PotentialRepeatReportAction.COPY_PRESENTATION),
      },
      {
        value: PotentialRepeatReportAction.COPY_SALES_ORDER,
        onClick: () =>
          handleActionItemClick(PotentialRepeatReportAction.COPY_SALES_ORDER),
      },
      {
        value: dismissedValue,
        onClick: () => handleActionItemClick(dismissedValue),
      },
    ];
  }, [dismissed, handleActionItemClick, isDismissedInAction]);

  const handleCloseNewProjectPopup = useCallback(() => {
    setShowNewProjectPopup(false);
    setNewProjectPopupData(null);
  }, []);

  const client = {
    id: client_id,
    name: client_name,
  } as Pick<DisplayClient, "id" | "name">;

  const message = {
    details_parent_id: job_id,
    details_parent_type: "JOB",
    source: POTENTIAL_REPEAT_REPORT_SOURCE,
    note_type: "TASK",
  };

  return (
    <>
      <StyledDropdownMenu options={actionDropdownOptions}>
        <MenuTrigger>
          <StyledActionsButton>Actions</StyledActionsButton>
        </MenuTrigger>
      </StyledDropdownMenu>

      {showNewProjectPopup && newProjectPopupData && (
        <NewProjectPopup
          client_id={newProjectPopupData.client_id}
          billing_contact_id={newProjectPopupData.billing_contact_id}
          prePopulatedData={newProjectPopupData}
          source={POTENTIAL_REPEAT_REPORT_SOURCE}
          onClose={handleCloseNewProjectPopup}
          />
      )}
      {taskPopup && (
        <CreateTaskPopup
          client={client}
          message={message}
          onCreate={() => {
            dispatch(
              setActionTaken({
                orderId: String(order_id),
                actionType: ProjectAction.CREATE_TASK,
                jobNumber: null,
                formNumber: null,
              })
            );
          }}
          onClose={() => {
            setTaskPopup(false);
          }}
        />
      )}
    </>
  );
}
